import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { ServiceCard, CourseCard } from '../components/Cards';
import MapView from '../components/MapView';
import BlogSection from '../components/BlogSection';
import { Users, Heart, BookOpen, GraduationCap, ChevronRight, Award, Sparkles, Clock, Share2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const { homeStats, fetchHomeStats } = useAppStore();
  const { isAuthenticated, getAuthHeaders } = useAuthStore();
  const language = i18n.language;
  
  const [userProfiles, setUserProfiles] = useState({ is_teacher: false, is_school: false });

  useEffect(() => {
    fetchHomeStats();
  }, [fetchHomeStats]);

  useEffect(() => {
    const loadProfiles = async () => {
      if (isAuthenticated) {
        try {
          const res = await axios.get(`${API_URL}/api/auth/profiles`, { headers: getAuthHeaders() });
          setUserProfiles(res.data);
        } catch (e) {
          console.error(e);
        }
      }
    };
    loadProfiles();
  }, [isAuthenticated, getAuthHeaders]);

  const stats = [
    { icon: Users, label: t('home.teachers'), count: homeStats?.counts?.teachers || 0 },
    { icon: BookOpen, label: t('home.schools'), count: homeStats?.counts?.schools || 0 },
    { icon: Heart, label: t('home.services'), count: homeStats?.counts?.services || 0 },
    { icon: GraduationCap, label: t('home.courses'), count: homeStats?.counts?.courses || 0 },
  ];

  const showProfessionalCTA = !isAuthenticated || (isAuthenticated && !userProfiles.is_teacher);
  const showSchoolCTA = !isAuthenticated || (isAuthenticated && !userProfiles.is_school);

  return (
    <div className="animate-fade-in" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative py-16 px-4 text-center bg-gradient-to-b from-cream-dark to-cream" style={{ background: 'linear-gradient(to bottom, #F5EFE6, #FDFBF7)' }}>
        <div className="max-w-4xl mx-auto">
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-primary mb-6" data-testid="hero-title">{t('home.welcome')}</h1>
          <p className="text-xl text-secondary mb-8" data-testid="hero-subtitle">{t('home.subtitle')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/sesiones">
              <Button className="bg-gold hover:bg-gold/90 text-white px-8 py-3 text-lg" style={{ backgroundColor: '#D4A574' }} data-testid="explore-services-btn">
                {t('home.exploreServices')}
              </Button>
            </Link>
            <Link to="/formacion-y-eventos">
              <Button variant="outline" className="border-gold text-gold hover:bg-gold hover:text-white px-8 py-3 text-lg" style={{ borderColor: '#D4A574', color: '#D4A574' }} data-testid="explore-courses-btn">
                {t('home.exploreCourses')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Interactive Map - Full Width */}
      <section className="py-8 px-4" data-testid="map-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-2xl font-bold text-primary mb-4 text-center">{t('home.findNearYou') || 'Encuentra profesionales cerca de ti'}</h2>
          <div className="rounded-2xl overflow-visible shadow-lg">
            <MapView compact={false} />
          </div>
        </div>
      </section>

      {/* Featured Services */}
      {homeStats?.featured?.services?.length > 0 && (
        <section className="py-12 px-4" data-testid="featured-services-section">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-serif text-3xl font-bold text-primary">{t('home.featuredServices')}</h2>
              <Link to="/sesiones" className="text-gold flex items-center hover:underline" style={{ color: '#D4A574' }}>
                {t('common.viewAll')} <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {homeStats.featured.services.map((service) => (
                <ServiceCard key={service.id} service={service} language={language} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Courses */}
      {homeStats?.featured?.courses?.length > 0 && (
        <section className="py-12 px-4 bg-cream-dark" style={{ backgroundColor: '#F5EFE6' }} data-testid="featured-courses-section">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-serif text-3xl font-bold text-primary">{t('home.featuredCourses')}</h2>
              <Link to="/formacion-y-eventos" className="text-gold flex items-center hover:underline" style={{ color: '#D4A574' }}>
                {t('common.viewAll')} <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {homeStats.featured.courses.map((course) => (
                <CourseCard key={course.id} course={course} language={language} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Call to Action - Join Platform */}
      {(showProfessionalCTA || showSchoolCTA) && (
        <section className="py-16 px-4 overflow-x-hidden" data-testid="cta-section">
          <div className="max-w-5xl mx-auto">
            {!isAuthenticated ? (
              /* CTA for non-authenticated users */
              <div className="bg-gradient-to-r from-primary to-primary/90 rounded-2xl md:rounded-3xl p-6 md:p-12 text-center text-white shadow-xl relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)' }}>
                {/* Launch Badge */}
                <div className="absolute top-0 right-0 bg-gold text-white text-xs font-bold px-3 md:px-4 py-1.5 md:py-2 rounded-bl-xl" style={{ backgroundColor: '#D4A574' }}>
                  <span className="flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    {t('cta.launch')}
                  </span>
                </div>
                
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center">
                    <Sparkles className="h-8 w-8 text-gold" style={{ color: '#D4A574' }} />
                  </div>
                </div>
                
                {/* Two-column layout for Professional and School CTAs */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
                  
                  {/* Professional CTA Card */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/20 text-left flex flex-col h-full">
                    {/* Micro-label */}
                    <div className="inline-block bg-gold/30 rounded-full px-3 py-1 mb-3 self-start">
                      <span className="text-gold text-xs font-semibold uppercase tracking-wide" style={{ color: '#D4A574' }}>
                        {t('cta.professionalMicroLabel')}
                      </span>
                    </div>
                    
                    {/* Title */}
                    <h3 className="font-serif text-2xl md:text-3xl font-bold mb-4 text-white">
                      {t('cta.professionalTitle')}
                    </h3>
                    
                    {/* Description - flex-grow to push content below to bottom */}
                    <p className="text-gray-300 mb-4 text-sm md:text-base leading-relaxed flex-grow">
                      {t('cta.professionalDescription')}
                    </p>
                    
                    {/* Bottom aligned content */}
                    <div className="mt-auto">
                      {/* Conversion line */}
                      <p className="text-white/90 text-sm font-medium mb-6 flex items-start gap-2">
                        <span className="text-gold mt-0.5" style={{ color: '#D4A574' }}>✨</span>
                        {t('cta.professionalConversion')}
                      </p>
                      
                      {/* CTA Button */}
                      <Link to="/register" className="block">
                        <Button className="w-full text-white px-6 py-3 text-base font-semibold hover:opacity-90 transition-opacity" style={{ backgroundColor: '#D4A574' }}>
                          <Award className="h-5 w-5 mr-2" />
                          {t('cta.registerProfessional')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                  
                  {/* School CTA Card */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/20 text-left flex flex-col h-full">
                    {/* Micro-label */}
                    <div className="inline-block bg-emerald-500/30 rounded-full px-3 py-1 mb-3 self-start">
                      <span className="text-emerald-300 text-xs font-semibold uppercase tracking-wide">
                        {t('cta.schoolMicroLabel')}
                      </span>
                    </div>
                    
                    {/* Title */}
                    <h3 className="font-serif text-2xl md:text-3xl font-bold mb-4 text-white">
                      {t('cta.schoolTitle')}
                    </h3>
                    
                    {/* Description - flex-grow to push content below to bottom */}
                    <p className="text-gray-300 mb-4 text-sm md:text-base leading-relaxed flex-grow">
                      {t('cta.schoolDescription')}
                    </p>
                    
                    {/* Bottom aligned content */}
                    <div className="mt-auto">
                      {/* Conversion line */}
                      <p className="text-white/90 text-sm font-medium mb-6 flex items-start gap-2">
                        <span className="text-emerald-400 mt-0.5">✨</span>
                        {t('cta.schoolConversion')}
                      </p>
                      
                      {/* CTA Button */}
                      <Link to="/register" className="block">
                        <Button variant="outline" className="w-full border-white text-white hover:bg-white hover:text-primary px-6 py-3 text-base font-semibold transition-all">
                          <BookOpen className="h-5 w-5 mr-2" />
                          {t('cta.registerSchool')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                  
                </div>
                
                {/* Limited Offer Footer */}
                <div className="border-t border-white/20 pt-4 mt-8 max-w-2xl mx-auto">
                  <p className="text-sm text-gray-400 flex items-center justify-center gap-2">
                    <Clock className="h-4 w-4" style={{ color: '#D4A574' }} />
                    <span>{t('cta.limitedOffer')} <strong className="text-white">{t('cta.freeRegistrationDuring')}</strong></span>
                  </p>
                </div>
              </div>
            ) : (
              /* CTA for authenticated users - show options based on missing profiles */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {showProfessionalCTA && (
                  <div className="rounded-2xl p-6 md:p-8 text-white text-left shadow-xl" style={{ background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)' }}>
                    {/* Micro-label */}
                    <div className="inline-block bg-gold/30 rounded-full px-3 py-1 mb-3">
                      <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#D4A574' }}>
                        {t('cta.professionalMicroLabel')}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(212, 165, 116, 0.2)' }}>
                        <Award className="h-6 w-6" style={{ color: '#D4A574' }} />
                      </div>
                      <h3 className="font-serif text-xl md:text-2xl font-bold">{t('cta.professionalTitle')}</h3>
                    </div>
                    
                    <p className="text-gray-300 mb-4 text-sm leading-relaxed">
                      {t('cta.professionalDescription')}
                    </p>
                    
                    <p className="text-white/90 text-sm font-medium mb-6 flex items-start gap-2">
                      <span style={{ color: '#D4A574' }}>✨</span>
                      {t('cta.professionalConversion')}
                    </p>
                    
                    <Link to="/become-teacher">
                      <Button className="w-full text-white font-semibold hover:opacity-90" style={{ backgroundColor: '#D4A574' }}>
                        {t('cta.registerProfessional')}
                      </Button>
                    </Link>
                  </div>
                )}
                
                {showSchoolCTA && (
                  <div className="rounded-2xl p-6 md:p-8 text-left shadow-xl" style={{ backgroundColor: '#F5EFE6' }}>
                    {/* Micro-label */}
                    <div className="inline-block bg-emerald-100 rounded-full px-3 py-1 mb-3">
                      <span className="text-emerald-700 text-xs font-semibold uppercase tracking-wide">
                        {t('cta.schoolMicroLabel')}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(212, 165, 116, 0.3)' }}>
                        <BookOpen className="h-6 w-6" style={{ color: '#D4A574' }} />
                      </div>
                      <h3 className="font-serif text-xl md:text-2xl font-bold text-primary">{t('cta.schoolTitle')}</h3>
                    </div>
                    
                    <p className="text-secondary mb-4 text-sm leading-relaxed">
                      {t('cta.schoolDescription')}
                    </p>
                    
                    <p className="text-primary/80 text-sm font-medium mb-6 flex items-start gap-2">
                      <span className="text-emerald-600">✨</span>
                      {t('cta.schoolConversion')}
                    </p>
                    
                    <Link to="/become-school">
                      <Button className="w-full text-white font-semibold hover:opacity-90" style={{ backgroundColor: '#D4A574' }}>
                        {t('cta.registerSchool')}
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Blog Section - Latest Articles */}
      <BlogSection />

      {/* Share Tantra Map Section */}
      <section className="py-12 px-4" data-testid="share-section">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Share2 className="h-6 w-6" style={{ color: '#D4A574' }} />
            <h2 className="font-serif text-2xl font-bold text-primary">
              {language === 'es' && '¡Comparte Tantra Map!'}
              {language === 'en' && 'Share Tantra Map!'}
              {language === 'it' && 'Condividi Tantra Map!'}
            </h2>
          </div>
          <p className="text-secondary mb-6">
            {language === 'es' && '¿Te gusta nuestra plataforma? Ayúdanos a crecer compartiéndola con tus amigos'}
            {language === 'en' && 'Do you like our platform? Help us grow by sharing it with your friends'}
            {language === 'it' && 'Ti piace la nostra piattaforma? Aiutaci a crescere condividendola con i tuoi amici'}
          </p>
          
          <div className="flex justify-center gap-3 flex-wrap">
            {/* Facebook */}
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://tantra-map.com')}&quote=${encodeURIComponent(language === 'es' ? 'Descubre Tantra Map - Tu directorio de profesionales y escuelas de tantra' : language === 'it' ? 'Scopri Tantra Map - La tua directory di professionisti e scuole di tantra' : 'Discover Tantra Map - Your directory of tantra professionals and schools')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-medium transition-transform hover:scale-105"
              style={{ backgroundColor: '#1877F2' }}
              data-testid="share-facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              Facebook
            </a>
            
            {/* Twitter/X */}
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(language === 'es' ? '🧘 Descubre Tantra Map - El directorio de profesionales y escuelas de tantra más completo' : language === 'it' ? '🧘 Scopri Tantra Map - La directory più completa di professionisti e scuole di tantra' : '🧘 Discover Tantra Map - The most complete directory of tantra professionals and schools')}&url=${encodeURIComponent('https://tantra-map.com')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-medium transition-transform hover:scale-105"
              style={{ backgroundColor: '#000000' }}
              data-testid="share-twitter"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              X
            </a>
            
            {/* WhatsApp */}
            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent((language === 'es' ? '🧘 Mira esta plataforma increíble para encontrar profesionales y escuelas de tantra: ' : language === 'it' ? '🧘 Guarda questa piattaforma incredibile per trovare professionisti e scuole di tantra: ' : '🧘 Check out this amazing platform to find tantra professionals and schools: ') + 'https://tantra-map.com')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-medium transition-transform hover:scale-105"
              style={{ backgroundColor: '#25D366' }}
              data-testid="share-whatsapp"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
            
            {/* LinkedIn */}
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://tantra-map.com')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-medium transition-transform hover:scale-105"
              style={{ backgroundColor: '#0A66C2' }}
              data-testid="share-linkedin"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              LinkedIn
            </a>
            
            {/* Telegram */}
            <a
              href={`https://t.me/share/url?url=${encodeURIComponent('https://tantra-map.com')}&text=${encodeURIComponent(language === 'es' ? '🧘 Descubre Tantra Map - Tu directorio de profesionales y escuelas de tantra' : language === 'it' ? '🧘 Scopri Tantra Map - La tua directory di professionisti e scuole di tantra' : '🧘 Discover Tantra Map - Your directory of tantra professionals and schools')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-medium transition-transform hover:scale-105"
              style={{ backgroundColor: '#0088cc' }}
              data-testid="share-telegram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
              Telegram
            </a>
          </div>
        </div>
      </section>

      {/* Stats - Compact Footer Style - Hidden until thresholds are met */}
      {/* Show only when: >50 professionals, >6 schools, >50 services, >20 courses */}
      {(homeStats?.counts?.teachers > 50 && 
        homeStats?.counts?.schools > 6 && 
        homeStats?.counts?.services > 50 && 
        homeStats?.counts?.courses > 20) && (
        <section className="py-8 px-4 bg-cream-dark border-t" style={{ backgroundColor: '#F5EFE6' }}>
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-center items-center gap-8 md:gap-16 flex-wrap">
              {stats.map((stat, idx) => (
                <div key={idx} className="flex items-center gap-3" data-testid={`stat-${idx}`}>
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <stat.icon className="h-5 w-5 text-gold" style={{ color: '#D4A574' }} />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-primary">{stat.count}</p>
                    <p className="text-xs text-secondary">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
